import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { HttpService } from '@services/http.service';
import { DashboardWidgetDataItem } from '@models/widget/widget.model';
import {
  AreaDashboard,
  DashboardByAreaResponse,
  DashboardPermissions,
  DashboardWidgetLayoutItem,
} from '@models/dashboard/dashboard.model';
import { WidgetMapData } from '@models/dashboard/dashboard-map.model';

@Injectable()
export class DashboardService extends HttpService {
  private readonly dashboardsUrl = `${environment.apiUrl}/dashboards`;

  createDashboardForArea(areaId: string): Observable<AreaDashboard> {
    return this.post(this.dashboardsUrl, { areaId }, this.createHeaders());
  }

  getDashboardSummaryByAreaId(areaId: string): Observable<DashboardByAreaResponse> {
    return this.get(`${this.dashboardsUrl}/area/${areaId}`, this.createHeaders());
  }

  getDashboardByUserAreaInstanceNode(
    userId: string,
    areaInstanceDocumentId: string,
    instanceId: string,
  ): Observable<AreaDashboard> {
    return this.get(
      `${this.dashboardsUrl}/users/${userId}/area-instance/${areaInstanceDocumentId}/instance/${instanceId}`,
      this.createHeaders(),
    );
  }

  getDashboardById(dashboardId: string): Observable<AreaDashboard> {
    return this.get(`${this.dashboardsUrl}/${dashboardId}`, this.createHeaders());
  }

  deleteDashboard(dashboardId: string): Observable<void> {
    const options = { ...this.createHeaders(), responseType: 'text' as const };
    return this.http.delete(`${this.dashboardsUrl}/${dashboardId}`, options).pipe(
      map(() => undefined),
      catchError((err) => this.handleError(err)),
    );
  }

  updatePermissions(
    dashboardId: string,
    permissions: Partial<DashboardPermissions>,
  ): Observable<DashboardPermissions & { id: string }> {
    return this.patch(`${this.dashboardsUrl}/${dashboardId}/permissions`, permissions, this.createHeaders());
  }

  saveDashboardLayout(dashboardId: string, layout: DashboardWidgetLayoutItem[]): Observable<void> {
    return this.patch(`${this.dashboardsUrl}/${dashboardId}/layout`, { layout }, this.createHeaders());
  }

  getDashboardWidgetsData(dashboardId: string): Observable<DashboardWidgetDataItem[]> {
    return this.get(`${this.dashboardsUrl}/${dashboardId}/data`, this.createHeaders()).pipe(
      catchError(() => of([])),
    );
  }

  getWidgetMapData(dashboardId: string, widgetId: string): Observable<WidgetMapData> {
    return this.get(`${this.dashboardsUrl}/${dashboardId}/widgets/${widgetId}/map-data`, this.createHeaders());
  }
}
